import React, { useEffect, useMemo, useState } from "react";
import "./AnnouncementPopup.css"; 

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production"
        ? "https://api.soaltriskelions.com" // your live backend URL
        : "http://localhost:5000");

const getToken = () => {
    try {
        return localStorage.getItem("token") || "";
    } catch {
        return "";
    }
};

const LS_KEY = "lastSeenAnnouncementId";

export default function AnnouncementPopup() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [idx, setIdx] = useState(0);

    // Fetch announcements on mount
    useEffect(() => {
        const loadAnnouncements = async () => {
            try {
                // Prevent showing popup again on page refresh
                const seenThisLogin = sessionStorage.getItem("ANN_POPUP_SHOWN");
                if (seenThisLogin) return;

                const res = await fetch(`${API_BASE}/api/announcements`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });

                const text = await res.text();
                let data;
                try {
                    data = JSON.parse(text);
                } catch {
                    console.error("❌ Invalid JSON from API:", text);
                    return;
                }

                if (!res.ok || !data.ok) return;

                
                const list = (data.rows || []).slice(); // Newest → Oldes
                /* const list = (data.rows || []).slice().reverse();  // Oldest → Newest */ 
                if (list.length === 0) return;

                // Show popup
                setItems(list);
                setOpen(true);
                setIdx(0);

                // Mark popup as shown for this login session
                sessionStorage.setItem("ANN_POPUP_SHOWN", "1");

            } catch (err) {
                console.error("❌ Failed to load announcements:", err);
            }
        };

        loadAnnouncements();
    }, []);


    const current = items[idx] || null;

    const close = () => {
        // Mark as seen so it won't reopen next login
        const newestId = items[0]?.id ?? 0;
        if (newestId) localStorage.setItem(LS_KEY, String(newestId));
        setOpen(false);
    };

    const next = () => setIdx((p) => Math.min(p + 1, items.length - 1));
    const prev = () => setIdx((p) => Math.max(p - 1, 0));

    const body = useMemo(() => {
        if (!current) return null;

        const fullPath = current.media_path?.startsWith("http")
            ? current.media_path
            : `${API_BASE}${current.media_path}`;

        if (current.media_type === "image" && current.media_path) {
            return (
                <img
                    src={fullPath}
                    alt=""
                    style={{
                        maxWidth: "100%",
                        borderRadius: 8,
                        border: "1px solid #333",
                    }}
                />
            );
        }

        if (current.media_type === "video" && current.media_path) {
            return (
                <video
                    controls
                    style={{ width: "100%", borderRadius: 8, border: "1px solid #333" }}
                >
                    <source src={fullPath} />
                </video>
            );
        }

        if (current.media_type === "pdf" && current.media_path) {
            return (
                <a
                    href={fullPath}
                    target="_blank"
                    rel="noreferrer"
                    className="announcement-link"
                >
                    View PDF
                </a>
            );
        }

        if (current.media_path) {
            return (
                <a
                    href={fullPath}
                    target="_blank"
                    rel="noreferrer"
                    className="announcement-link"
                >
                    Open Attachment
                </a>
            );
        }

        return null;
    }, [current]);


    if (!open || !current) return null;

    return (
        <div className="announcement-overlay" onClick={close}>
            <div className="announcement-box" onClick={(e) => e.stopPropagation()}>
                <button className="announcement-close" onClick={close}>
                    ✖
                </button>

                <h2>{current.title}</h2>
                <div className="announcement-body">
                    {current.message && <p>{current.message}</p>}
                    <div>{body}</div>
                </div>

                {items.length > 1 && (
                    <div className="announcement-nav">
                        <button onClick={prev} disabled={idx === 0}>
                            ◀ Prev
                        </button>
                        <span>
                            {idx + 1} / {items.length}
                        </span>
                        <button onClick={next} disabled={idx === items.length - 1}>
                            Next ▶
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
